import { shortEn } from "@/lib/constants";
import useNotes from "@/src/notes/hooks/useNotes";
import { ClockIcon } from "@heroicons/react/24/outline";
import { ChatBubbleOvalLeftIcon, StarIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import humanizeDuration from "humanize-duration";

humanizeDuration.languages.shortEn = shortEn;

const Meta = ({ recipe, ratingValue, totalNotesIncludingReplies = 0 }) => {
  const { time } = recipe;

  return (
    <div
      className={clsx("RecipeHero_meta", "flex w-full items-center text-base")}
    >
      <div className={clsx("flex items-center space-x-3")}>
        <_RatingIndicator ratingValue={ratingValue} />

        <>
          <div className={clsx("h-5 w-px bg-current opacity-20")} />

          <_NoteIndicator
            elementId={parseInt(recipe.id)}
            initialTotalNotesIncludingReplies={totalNotesIncludingReplies}
          />
        </>
      </div>
      <div className={clsx("pl-8")}>
        <_TimeIndicator time={time} />
      </div>
    </div>
  );
};

const _TimeIndicator = ({ time }) => {
  const humanizedTime = humanizeDuration(time * 60000, {
    largest: 1,
    units: ["h", "m"],
    round: true,
    language: "shortEn",
  });

  return (
    <div className={clsx("flex items-center space-x-1 font-body text-sm")}>
      <div>
        <ClockIcon className={clsx("size-4")} />
      </div>
      <div>{humanizedTime} cook</div>
    </div>
  );
};

const _RatingIndicator = ({ ratingValue }: { ratingValue: number }) => (
  <_Indicator icon={StarIcon} value={ratingValue} targetId="reaction" />
);

const _NoteIndicator = ({
  elementId,
  initialTotalNotesIncludingReplies,
}: {
  elementId: number;
  initialTotalNotesIncludingReplies: number;
}) => {
  const { totalNotesIncludingReplies, isLoading } = useNotes({
    elementId,
  });

  // While we're loading, our total notes is initialTotalNotesIncludingReplies until totalNotesIncludingReplies is ready
  const totalNotes = isLoading
    ? initialTotalNotesIncludingReplies
    : totalNotesIncludingReplies;

  return (
    <_Indicator
      icon={ChatBubbleOvalLeftIcon}
      value={totalNotes}
      targetId="reaction"
      label={`Note${totalNotesIncludingReplies !== 1 ? "s" : ""}`}
    />
  );
};

type IndicatorProps = {
  icon: React.ElementType;
  value: number | string;
  targetId: string;
  label?: string;
};

const _Indicator = ({
  icon: Icon,
  value,
  targetId,
  label = null,
}: IndicatorProps) => {
  const handleClick = () => {
    document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className={clsx(
        "flex cursor-pointer items-center space-x-1.5 transition ease-out hover:opacity-75"
      )}
      onClick={handleClick}
    >
      <div>
        <Icon className={clsx("size-4")} />
      </div>
      <div className={clsx("tabular-nums")}>{value}</div>
      {!!label && <div>{label}</div>}
    </div>
  );
};

export default Meta;
